'use client';

import { Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { ArrowRight, CheckCircleRounded } from '@mui/icons-material';
import {
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from '@mui/material';
import {
  CascadingHoverMenus,
  CascadingMenuItem,
} from '@prismo-io/design-system';
import { useMe } from '@prismo-io/feature-auth/providers/me';
import { Application, translate } from '@prismo-io/router';
import {
  LanguageCode,
  type LanguageCodeT,
  type UserAccessTypeT,
} from '@prismo-io/schemas';
import { useFragment } from '@prismo-io/utils/client/use-fragment';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';

type Props = {
  userUrl: string;
  access: UserAccessTypeT;
  order?: number;
  isCompleted?: boolean;
  disabled?: boolean;
  showAura?: boolean;
  showHelio?: boolean;
  showQuark?: boolean;
};

export const HistoricItem: FC<Props> = (props: Props) => {
  const {
    userUrl,
    isCompleted = false,
    access,
    disabled = false,
    showAura = true,
    showHelio = true,
    showQuark = true,
    order = 5,
  } = props;

  const { i18n } = useLingui();
  const { locale = LanguageCode.Enum.fr } = i18n;

  const me = useMe();

  const isMe = me && me.url === userUrl;

  const pathname = usePathname();
  const fragment = useFragment();

  const baseUrl = useMemo(
    () =>
      translate({
        app: Application.APP,
        path: '/profil/:user/historique',
        params: {
          user: userUrl,
        },
        locale: locale as LanguageCodeT,
      }),
    [locale, userUrl]
  );

  const selected = useMemo(() => {
    return pathname.startsWith(baseUrl);
  }, [baseUrl, pathname]);

  return (
    <CascadingHoverMenus
      variant="popover"
      cascadingMenuProps={{
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      }}
      button={(props) => (
        <MenuItem
          {...props}
          selected={selected}
          component={Link}
          href={baseUrl}
          sx={{ order, paddingY: 2 }}
          disabled={!isCompleted || disabled}
        >
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            {isCompleted && (
              <CheckCircleRounded color="primary" fontSize="small" />
            )}
            {!isCompleted && (
              <Box
                component="div"
                borderRadius={9999}
                width="24px"
                height="24px"
                display="flex"
                alignItems="center"
                justifyItems="center"
                color="primary.main"
                sx={{
                  borderStyle: 'solid',
                  borderWidth: '2px',
                  borderColor: 'primary.main',
                }}
              >
                <Typography
                  sx={{ flex: 1 }}
                  color="primary"
                  fontSize="small"
                  fontWeight="bolder"
                  textAlign="center"
                >
                  {order}
                </Typography>
              </Box>
            )}
          </ListItemIcon>
          <ListItemText sx={{ flex: 1 }}>
            <Typography display="inline">
              <Trans>Évolution du profil</Trans>
            </Typography>
          </ListItemText>
          <ListItemIcon sx={{ minWidth: '12px !important' }}>
            <ArrowRight fontSize="small" />
          </ListItemIcon>
        </MenuItem>
      )}
      popupId="history-menu"
    >
      {showAura && (
        <CascadingMenuItem
          component={Link}
          href={`${baseUrl}#history-1`}
          selected={'#history-1' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Évolution du profil comportemental Aura</Trans>
        </CascadingMenuItem>
      )}

      {showHelio && (
        <CascadingMenuItem
          component={Link}
          href={`${baseUrl}#history-2`}
          selected={'#history-2' === fragment}
          disabled={!isCompleted}
        >
          <Trans>Évolution du profil motivationnel Helio</Trans>
        </CascadingMenuItem>
      )}

      {showQuark && (
        <CascadingMenuItem
          component={Link}
          href={`${baseUrl}#history-3`}
          selected={'#history-3' === fragment}
          disabled={!isCompleted}
        >
          {/* <Trans>Tendances potentielles d’évolution professionnelle</Trans> */}
          <Trans>Évolution des compétences Quark</Trans>
        </CascadingMenuItem>
      )}
    </CascadingHoverMenus>
  );
};
